import { Anchor } from '@mantine/core';
import { IconFileTypePdf, IconPhoto } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { AttachmentFileActions } from '../../attachments/AttachmentFileActions.js';
import { formatDateTime } from '../../format.js';
import work from './PmsWork.module.css';
import { PmsBadge } from './PmsWorkParts.js';
import type { PmsAttachment } from './types.js';

/** Dosya boyutu: KB ya da MB, bir ondalık. */
export function fileSize(bytes: number, language: string): string {
  const format = new Intl.NumberFormat(language === 'tr' ? 'tr-TR' : 'en-GB', {
    maximumFractionDigits: 1,
  });
  return bytes >= 1024 * 1024
    ? `${format.format(bytes / (1024 * 1024))} MB`
    : `${format.format(Math.max(1, Math.round(bytes / 1024)))} KB`;
}

/**
 * Ek kartı (K15): ad, tür, boyut ve zaman. Ofiste dosya gemiden gelmediyse "Uploading from
 * vessel", aktarım başlamadıysa (dosyayla senkronlanan gemi) "File on vessel"; gelmişse açılır
 * (409 `FILE_NOT_UPLOADED`). İptal edilen ek "Voided" ile geçmişte kalır [NR496 4.3.2a].
 *
 * Açılabilen ekin yanında "göz" (önizleme) ve indirme simgeleri (ortak 2026-10-24 madde 4; ADR
 * 0126): adres ekranın kaynağından (ofis) ya da uygulamanın ek erişiminden (gemi: gemi
 * sunucusu) gelir. Gemide ek dosyası gemi sunucusundadır; `officeState` yoktur.
 */
export function AttachmentCard({
  attachment,
  href,
}: {
  attachment: PmsAttachment;
  /**
   * Açma adresi (ofis). Verilmezse uygulamanın ek erişimi (gemi); `null`: dosyanın ek ucu yok
   * (ör. ofisin bordro dekontu gemide), simge çizilmez.
   */
  href?: string | null | undefined;
}) {
  const { t, i18n } = useTranslation();
  const k = (key: string) => t(`pms.work.attachments.${key}`);
  const pdf = attachment.mimeType === 'application/pdf';
  const state = attachment.officeState;
  const available = !attachment.voided && (state === undefined || state === 'verified');
  const openable = typeof href === 'string' && state === 'verified' && !attachment.voided;
  return (
    <li className={work.attachment} data-voided={attachment.voided || undefined}>
      <span className={work.attachmentIcon} aria-hidden>
        {pdf ? <IconFileTypePdf size={20} /> : <IconPhoto size={20} />}
      </span>
      <span className={work.attachmentBody}>
        {openable ? (
          <Anchor href={href} target="_blank" rel="noopener" className={work.attachmentName}>
            {attachment.fileName}
          </Anchor>
        ) : (
          <span className={work.attachmentName}>{attachment.fileName}</span>
        )}
        <span className={work.attachmentMeta}>
          {t(`pms.work.attachments.kinds.${attachment.kind}`, { defaultValue: attachment.kind })}
          {' · '}
          {fileSize(attachment.size, i18n.resolvedLanguage ?? 'en')}
          {' · '}
          {formatDateTime(attachment.createdAt)}
        </span>
        {attachment.caption ? (
          <span className={work.attachmentMeta}>{attachment.caption}</span>
        ) : null}
      </span>
      {available ? (
        <AttachmentFileActions
          id={attachment.id}
          fileName={attachment.fileName}
          mimeType={attachment.mimeType}
          href={href}
        />
      ) : null}
      {attachment.voided ? (
        <PmsBadge status="voided">{k('voided')}</PmsBadge>
      ) : state === 'uploading' ? (
        <PmsBadge status="pending">{k('uploading')}</PmsBadge>
      ) : state === 'on_vessel' ? (
        <PmsBadge status="scheduled">{k('onVessel')}</PmsBadge>
      ) : null}
    </li>
  );
}
